// src/components/aiTalentDrive/driveMotion.tsx
// Motion pieces for the /ai-talent-drive page ("Broadcast signal" redesign).
// Framer Motion for scroll/velocity-linked motion, plain DOM for the count-up.
// Every piece drops to a still, fully readable state under prefers-reduced-motion.
import React, { useEffect, useLayoutEffect, useRef } from "react";
import {
  motion,
  useAnimationFrame,
  useMotionValue,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform,
  useVelocity,
} from "framer-motion";

/* ---------- Magnetic button: leans toward a mouse pointer ---------- */
export function MagneticButton({
  children,
  className,
  onClick,
  disabled,
}: {
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
  disabled?: boolean;
}) {
  const ref = useRef<HTMLButtonElement>(null);
  const reduce = useReducedMotion();
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 220, damping: 18, mass: 0.4 });
  const sy = useSpring(y, { stiffness: 220, damping: 18, mass: 0.4 });

  const onMove = (e: React.PointerEvent) => {
    if (reduce || e.pointerType !== "mouse" || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    x.set((e.clientX - (r.left + r.width / 2)) * 0.28);
    y.set((e.clientY - (r.top + r.height / 2)) * 0.28);
  };
  const onLeave = () => {
    x.set(0);
    y.set(0);
  };

  return (
    <motion.button
      ref={ref}
      type="button"
      className={className}
      disabled={disabled}
      style={{ x: sx, y: sy }}
      onPointerMove={onMove}
      onPointerLeave={onLeave}
      onClick={onClick}
    >
      {children}
    </motion.button>
  );
}

/* ---------- Velocity marquee: drifts on its own, scroll speed pushes it ---------- */
const wrap = (min: number, max: number, v: number) => {
  const range = max - min;
  return ((((v - min) % range) + range) % range) + min;
};

const Spark = () => (
  <svg className="td-spark" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 0l2.6 9.4L24 12l-9.4 2.6L12 24l-2.6-9.4L0 12l9.4-2.6z" />
  </svg>
);

export function VelocityMarquee({ items, base = -0.5 }: { items: string[]; base?: number }) {
  const reduce = useReducedMotion();
  const { scrollY } = useScroll();
  const smooth = useSpring(useVelocity(scrollY), { damping: 50, stiffness: 400 });
  // scroll speed nudges the drift, capped so a fast flick cannot whip it along
  const factor = useTransform(smooth, [-1000, 0, 1000], [-2, 0, 2]);
  const x = useMotionValue(0);
  const xPct = useTransform(x, (v) => `${v}%`);
  const dir = useRef(1);

  useAnimationFrame((_, delta) => {
    if (reduce) return;
    let move = dir.current * base * (delta / 1000);
    const f = factor.get();
    if (f < 0) dir.current = -1;
    else if (f > 0) dir.current = 1;
    move += dir.current * move * f;
    // the row is repeated four times, so one copy is 25% of the track
    x.set(wrap(-25, 0, x.get() + move));
  });

  const row = items.map((name) => (
    <span className="td-marquee-item" key={name}>
      {name}
      <Spark />
    </span>
  ));

  return (
    <div className="td-marquee" aria-hidden="true">
      <motion.div className="td-marquee-track" style={{ x: xPct }}>
        {[0, 1, 2, 3].map((n) => (
          <div className="td-marquee-row" key={n}>
            {row}
          </div>
        ))}
      </motion.div>
    </div>
  );
}

/* ---------- Count-up: "26,000+" counts up once it scrolls into view ---------- */
// The final text is always in the DOM (so it is what gets prerendered and read);
// it is only hidden, then re-written as it counts.
export function CountUp({ value }: { value: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const reduce = useReducedMotion();

  useLayoutEffect(() => {
    if (!reduce && "IntersectionObserver" in window) ref.current?.classList.add("pre");
  }, [reduce]);

  useEffect(() => {
    const el = ref.current;
    const m = value.match(/^(\D*)([\d,]+)(.*)$/);
    if (!el || !m || reduce || !("IntersectionObserver" in window)) return;
    const [, pre, digits, post] = m;
    const target = Number(digits.replace(/,/g, ""));
    let raf = 0;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        io.disconnect();
        const t0 = performance.now();
        const step = (t: number) => {
          const p = Math.min(1, (t - t0) / 1600);
          const eased = 1 - Math.pow(1 - p, 3);
          el.textContent = pre + Math.round(target * eased).toLocaleString("en-US") + post;
          if (p < 1) raf = requestAnimationFrame(step);
        };
        el.textContent = pre + "0" + post;
        el.classList.remove("pre");
        raf = requestAnimationFrame(step);
      },
      { threshold: 0.6 }
    );
    io.observe(el);
    return () => {
      io.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [value, reduce]);

  return (
    <span ref={ref} className="td-count">
      {value}
    </span>
  );
}

/* ---------- Pain points: the four questions, stacked beside the headline ---------- */
type PainItem = { icon: React.ElementType; text: string };

export function PainLines({ items }: { items: PainItem[] }) {
  return (
    <ul className="td-pain-list hc-stagger">
      {items.map(({ icon: Icon, text }) => (
        <li className="td-pain-line" key={text}>
          <Icon className="td-pain-icon" size={40} weight="duotone" aria-hidden="true" />
          <p>{text}</p>
        </li>
      ))}
    </ul>
  );
}
